import Immutable from 'immutable';
import {
  aggregateAndGroupGoals,
  aggregateAndGroupRegistrations,
  aggregateStreaks,
  filterAwayGoalExercises,
  initializeRegistrationDates,
} from '../../actions/Registrations';
import {
  CLIENTS_FETCHED,
  CLIENT_EXERCISES_LOADED,
  CLIENT_REGISTRATIONS_LOADED,
  EXERCISE_FIELDS_LOADED,
  NURSE_PRACTITIONER_INFO_LOADED,
  NURSE_PRACTITIONER_UNPAIRED,
  PROGRAM_LOADED,
  RESET_NURSE_PRACTITIONER,
} from '../../actions/NursePractitioner';

const initialState = Immutable.fromJS({
  info: null,
  clients: [],
  clientDetails: {},
  program: {},
});

export default function nursePractitionerReducer(state = initialState, action = {}) {
  switch (action.type) {
    case NURSE_PRACTITIONER_INFO_LOADED: {
      return state.set('info', Immutable.fromJS(action.data));
    }

    case CLIENTS_FETCHED: {
      const { data } = action;

      const clientsList = data.map((clientData) => {
        const weeks_registered = Math.max(1, Math.ceil(clientData['days_registered'] / 7));

        return {
          ...clientData,
          weeks_registered,
        };
      });

      return state.set('clients', Immutable.fromJS(clientsList));
    }

    case CLIENT_REGISTRATIONS_LOADED: {
      const { data } = action;
      const clients = state.get('clients', Immutable.List());

      if (clients.size === 0) {
        return state;
      }

      const currentClient = clients.find((client) => client.get('uid') === data.uid);

      if (!currentClient) {
        return state;
      }

      const registrationsStartDate = currentClient.get('registrations_start_date', '');
      const programStartDate = currentClient.get('program_start_date', '');

      const initializedRegistrations = initializeRegistrationDates(registrationsStartDate, programStartDate);
      const registrations = aggregateAndGroupRegistrations(data.entities, initializedRegistrations);

      return state.setIn(['clientDetails', data.domain, data.uid], {
        registrations: Immutable.OrderedMap(registrations),
        programStartDate,
      });
    }

    case CLIENT_EXERCISES_LOADED: {
      const exerciseData = action.data;
      const { uid, domain } = exerciseData;

      const { programStartDate, registrations } = state.getIn(['clientDetails', domain, uid]);

      const aggregatedRegistrations = aggregateAndGroupGoals(exerciseData.entities, programStartDate, registrations.toJS());

      const streaks = aggregateStreaks(aggregatedRegistrations);

      const completedExercises = filterAwayGoalExercises(exerciseData.entities);

      let groupedExercises = {};

      completedExercises.forEach((exercise) => {
        const webformId = exercise.webform_id[0].target_id;

        groupedExercises[webformId] = exercise;
      });

      return state.setIn(['clientDetails', domain, uid], {
        registrations: Immutable.OrderedMap(aggregatedRegistrations),
        completed_exercises: Immutable.fromJS(groupedExercises),
        streaks,
      });
    }

    case PROGRAM_LOADED: {
      const { data } = action;
      const { domain, ...program } = data;

      const allowedCategories = ['list', 'quiz'];

      const phases = program.field_paragraphs.map(({ entity }) => {
        const phase = entity.field_exercise.map(({ entity }) => entity);

        const exercises = phase
          .filter((exercise) => exercise.field_webform.length)
          .filter((exercise) => {
            if (exercise.field_webform[0].category) {
              return allowedCategories.includes(exercise.field_webform[0].category)
            }

            return exercise;
          })
          .map((exercise) => {
            return {
              ...exercise.field_webform[0],
              icon: exercise.field_icon[0] && exercise.field_icon[0].value,
              title: exercise.field_title[0] && exercise.field_title[0].value,
            };
          });

        const exerciseKeys = exercises.map((exercise) => exercise.target_id);

        let exerciseData = {};

        exerciseKeys.forEach((key) => {
          exerciseData[key] = exercises.find((exercise) => exercise.target_id === key);
        });

        return exerciseData;
      });

      return state.setIn(['program', domain], Immutable.fromJS(phases));
    }

    case EXERCISE_FIELDS_LOADED: {
      const { data } = action;
      const { domain, phases } = data;

      const phasesFlat = phases.flat();

      const program = state.getIn(['program', domain]).toJS();

      let writableProgram = [];

      program.forEach((phase, index) => {
        const exerciseIds = Object.keys(phase);

        exerciseIds.forEach((exerciseId) => {
          const matchingExercise = phasesFlat.find((exercise) => exercise.webformId === exerciseId);

          if (matchingExercise) {
            const { domain, webformId, ...fields } = matchingExercise;

            const isWritable = recursivelyCheckWebformEligibility(fields);

            if (isWritable && writableProgram[index]) {
              writableProgram[index] = {
                ...writableProgram[index],
                [webformId]: {
                  ...program[index][webformId],
                  fields,
                },
              };
              return;
            }

            if (isWritable) {
              writableProgram.push({
                [webformId]: {
                  ...program[index][webformId],
                  fields,
                },
              });
            }
          }
        });
      });

      return state.setIn(['program', domain], Immutable.fromJS(writableProgram));
    }

    case NURSE_PRACTITIONER_UNPAIRED: {
      return state.set('info', null);
    }

    case RESET_NURSE_PRACTITIONER: {
      return initialState;
    }

    default:
      return state;
  }
}

function getEligibleKeys(webformData) {
  const keys = Object.keys(webformData)
    // filter out keys starting with pound signs
    .filter((key) => key.at(0) !== '#')
    // filter out keys that aren't objects
    .filter((key) => {
      const property = webformData[key];
      const isObject = typeof property === 'object' && !Array.isArray(property);
      return isObject;
    });

  return keys;
}

function recursivelyCheckWebformEligibility(item) {
  const disallowedFields = ['processed_text', 'webform_wizard_page', 'datetime', 'webform_entity_view', 'webform_actions'];
  const eligibleKeys = getEligibleKeys(item);
  const webformType = item['#type'];

  if (!eligibleKeys.length && !webformType) {
    return false;
  }

  if (webformType && !disallowedFields.includes(webformType)) {
    return true;
  }

  return eligibleKeys.some((key) => recursivelyCheckWebformEligibility(item[key]));
}
